import { forwardRef } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { CONTROL_BASE, CONTROL_SIZE } from './Input';
import type { ControlSize } from './Input';
import { useFieldControl } from './Field';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  selectSize?: ControlSize;
  /** Convenience: renders these instead of `children`. */
  options?: SelectOption[];
  /** Adds a leading disabled option, e.g. "Selecione uma pista". */
  placeholder?: string;
  invalid?: boolean;
  wrapperClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    selectSize = 'md',
    options,
    placeholder,
    invalid,
    className,
    wrapperClassName,
    id,
    required,
    children,
    ...rest
  },
  ref,
) {
  const field = useFieldControl({
    id,
    required,
    'aria-invalid': invalid ? true : undefined,
    'aria-describedby': rest['aria-describedby'],
  });

  return (
    <div className={cn('relative flex items-center', wrapperClassName)}>
      <select
        ref={ref}
        {...rest}
        {...field}
        className={cn(CONTROL_BASE, CONTROL_SIZE[selectSize], 'appearance-none pl-3 pr-9', className)}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options
          ? options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))
          : children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3 size-4 text-text-faint"
      />
    </div>
  );
});
